import athenaConfig from '../config';
import { APP_TYPES, CLIENTID_ATHENAONE_HOST_MAPPING } from '../constants';
import { AthenaOneParams } from './banner-jwt-utils';
import { withErrorConsumed } from './common-utils';

const { athenaOneWebClientIds, athenaOneMobileClientIds, env } =
  athenaConfig;

export function isAthenaOneWebClient(): boolean {
  return athenaOneWebClientIds.includes(settings.clientId);
}

export function isAthenaOneWebOrMobileClient(): boolean {
  return isAthenaOneWebClient() || athenaOneMobileClientIds.includes(settings.clientId);
}

export function isDirectOhswAdjacentAuthClient(): boolean {
  return [
    APP_TYPES.ADJACENT_AONE_ATHENISTA_AUTH,
    APP_TYPES.ADJACENT_ATHENISTA,
  ].includes(settings.appType!);
}

export function getAthenaOneHost(clientId?: string) {
  const { defaultAthenaOneHost } = athenaConfig;
  let referrerHost;
  //If we are iframed, this should be set, so use this to support dev servers
  if (document.referrer) {
    referrerHost = new URL(document.referrer).host;
  }
  let athenaOneHost;
  if (clientId) {
    const referrerHostAllowed = referrerHost && CLIENTID_ATHENAONE_HOST_MAPPING[clientId]?.includes(referrerHost);
    athenaOneHost = referrerHostAllowed ? referrerHost : CLIENTID_ATHENAONE_HOST_MAPPING[clientId]?.[0];
  }
  return athenaOneHost || (env !== 'prod' && referrerHost) || defaultAthenaOneHost;
}

const _getBasePathForAthenaOneUrl = (athenaOneParams?: AthenaOneParams) => {
  const practiceId = athenaOneParams?.PRACTICEID;
  const stackNumber = athenaOneParams?.STACKNUMBER;
  let basePath = '1/1';
  if (practiceId && stackNumber) {
    basePath = `${practiceId}/${stackNumber}`;
  }
  return basePath;
};

export function getAthenaOneSwitchUserUrl(clientId?: string, athenaOneParams?: AthenaOneParams) {
  const athenaOneHost = getAthenaOneHost(clientId);
  const reAuthUrl = new URL(`https://${athenaOneHost}`);
  const oktaIdpId = athenaOneParams?.SSOREAUTHPARAMS?.OKTAIDPID;
  const basePath = _getBasePathForAthenaOneUrl(athenaOneParams);
  reAuthUrl.pathname = `${basePath}/logout.esp`;
  if (oktaIdpId) {
    const loginUrl = `/${basePath}/login/startoidc.esp?IDP=${oktaIdpId}&PROMPT=login`;
    reAuthUrl.searchParams.set('LOGINURL', loginUrl);
  }
  else {
    reAuthUrl.searchParams.set('FORCELOGIN', '1');
  }
  return reAuthUrl.toString();
}

export function getAthenaOneSsoReauthUrl(clientId: string, athenaOneParams: AthenaOneParams) {
  const reAuthUrl = new URL(`https://${getAthenaOneHost(clientId)}`);
  const basePath = _getBasePathForAthenaOneUrl(athenaOneParams);
  reAuthUrl.pathname = `${basePath}/login/oidc.esp`;
  const { SSOREAUTHPARAMS: ssoReauthParams } = athenaOneParams;
  ssoReauthParams &&
    Object.keys(ssoReauthParams).forEach((param) => {
      reAuthUrl.searchParams.set(param, encodeURIComponent(ssoReauthParams[param]));
    });
  if (athenaOneParams.PRACTICEID) {
    reAuthUrl.searchParams.set('PRACTICEID', athenaOneParams.PRACTICEID);
  }
  reAuthUrl.searchParams.set('REDIRECT', '1');
  reAuthUrl.searchParams.set('RESUMESESSION', '1');

  return reAuthUrl;
}

export function getAthenaOneSSOLoginTimedOutUrl(athenaOneParams: AthenaOneParams) {
  const url = getAthenaOneSSOLoginUrl(athenaOneParams)!;
  url.searchParams.delete('prompt');
  return url;
}

export function getAthenaOneSSOLoginUrl(athenaOneParams: AthenaOneParams) {
  if (athenaOneParams.SSOREAUTHPARAMS?.OKTAIDPID) {
    const url = new URL(window.location.href);
    url.searchParams.delete('login_hint');
    url.searchParams.delete('BANNERARGS');
    url.searchParams.set('idp', athenaOneParams.SSOREAUTHPARAMS?.OKTAIDPID);
    return url;
  }
}
export function getNonSsoReauthUrl(clientId: string, athenaOneParams: AthenaOneParams) {
  const reAuthUrl = new URL(`https://${getAthenaOneHost(clientId)}`);
  const basePath = _getBasePathForAthenaOneUrl(athenaOneParams);
  reAuthUrl.pathname = `${basePath}/login/oidc.esp`;
  const { NONSSOREAUTHPARAMS: nonSsoReauthParams } = athenaOneParams;
  nonSsoReauthParams &&
    Object.keys(nonSsoReauthParams).forEach((param) => {
      reAuthUrl.searchParams.set(param, encodeURIComponent(nonSsoReauthParams[param]));
    });
  if (athenaOneParams.PRACTICEID) {
    reAuthUrl.searchParams.set('PRACTICEID', athenaOneParams.PRACTICEID);
  }
  reAuthUrl.searchParams.set('REDIRECT', '1');
  reAuthUrl.searchParams.set('RESUMESESSION', '1');
  // SHOWBANNER drives the second-leg authorize URL (built by GetRedirectURL on oidc.esp) to attach
  // the username as login_hint, so the popup shows the reauth form with the username pre-filled.
  reAuthUrl.searchParams.set('SHOWBANNER', athenaOneParams.BANNERTYPE ?? 'TIMEDOUT');
  return reAuthUrl;
}

// Below banner types indicate session was killed and widget was launched within an iframe
const sessionTimedOutBannerType = (bannerType?: string): boolean => {
  return [ 'TIMEDOUT', 'SESSIONLOCKED', 'CONCIERGEMODE', 'REFRESHTIMEOUT' ].includes(bannerType ?? '');
};

const ssoParamsPresent = (athenaOneParams?: AthenaOneParams): boolean => {
  return !!(athenaOneParams?.SSOREAUTHPARAMS && Object.keys(athenaOneParams.SSOREAUTHPARAMS).length);
};

const nonSsoParamsPresent = (athenaOneParams?: AthenaOneParams): boolean => {
  return !!(athenaOneParams?.NONSSOREAUTHPARAMS && Object.keys(athenaOneParams.NONSSOREAUTHPARAMS).length);
};

export const isAthenaOneWebSessionTimedOut = (athenaOneParams?: AthenaOneParams): boolean => {
  return sessionTimedOutBannerType(athenaOneParams?.BANNERTYPE) && !ssoParamsPresent(athenaOneParams);
};

const popupReauthBannerType = (bannerType?: string): boolean => {
  return [ 'TIMEDOUT', 'REFRESHTIMEOUT', 'SESSIONLOCKED', 'CONCIERGEMODE' ].includes(bannerType ?? '');
};

// True only for the first leg, where the monolith emits NONSSOREAUTHPARAMS to signal that
// reauth should launch a popup. The second leg (inside the popup, hitting Okta) intentionally
// has no NONSSOREAUTHPARAMS so the widget shows the inline timeout experience instead.
export const isAthenaOneWebNonSsoReauthViaPopup = (athenaOneParams?: AthenaOneParams): boolean => {
  return nonSsoParamsPresent(athenaOneParams) && popupReauthBannerType(athenaOneParams?.BANNERTYPE);
};

export const isAthenaOneWebSso = (athenaOneParams?: AthenaOneParams): boolean => {
  return ssoParamsPresent(athenaOneParams);
};

export interface HostClientIdMapping {
  host: string;
  clientId: string;
}

export const PASSWORD_RESET_URL_TO_CLIENT_MAPPING: Record<string, HostClientIdMapping[]> = {
  '/signin/forgot-password': [
    { host: 'identity.athenahealth.com', clientId: '0oaet0rfjNzyKCiQQ296' }, // prod
    { host: 'identity.dev.athenahealth.com', clientId: '0oa554hzbhSbGogvt1t7' }, // dev
    { host: 'identity.int.athenahealth.com', clientId: '0oac0y9mixYkeU8oH0h7' }, // int
    { host: 'identity.preprod.athenahealth.com', clientId: '0oaxb4e21s48Yg87x297' }, // preprod
  ], // athenaNet
  '/signin/forgot-password-preview': [ { host: 'identity.athenahealth.com', clientId: '0oa16evw7trKLV80X297' } ], // athenaNet - Preview
  '/signin/forgot-password-salesdemo': [ { host: 'identity.athenahealth.com', clientId: '0oa1g3pp3dNEa1Rhm297' } ], // athenaNet - salesdemo
  '/signin/forgot-password-sandbox': [ { host: 'identity.athenahealth.com', clientId: '0oa1g3qt4lLFlpa7d297' } ], // athenaNet - Sandbox
  '/signin/forgot-password-clientsandbox': [ { host: 'identity.athenahealth.com', clientId: '0oa4zsgxk7LR1gMAo297' } ], // athenaNet - ClientSandbox
  '/signin/forgot-password-clienttrain': [ { host: 'identity.athenahealth.com', clientId: '0oa1g3qnz9iqZWIjR297' } ], // athenaNet - ClientTrain
};

export function getPasswordResetUrlByClientId(clientId: string): string | null {
  const currentHost = window.location.host;

  // Search through all URL patterns to find matching clientId
  for (const [ urlPattern, hostMappings ] of Object.entries(PASSWORD_RESET_URL_TO_CLIENT_MAPPING)) {
    const hostConfig = hostMappings.find(mapping =>
      mapping.host === currentHost && mapping.clientId === clientId
    );

    if (hostConfig) {
      const targetUrl = new URL(window.location.origin + urlPattern);
      targetUrl.searchParams.set('client_id', clientId);
      return targetUrl.toString();
    }
  }

  return null;
}

export const isAthenaOneWebLogout = (athenaOneParams?: AthenaOneParams): boolean => {
  return athenaOneParams?.BANNERTYPE === 'LOGOUT';
};

export const isNonSsoLoginAttempt = withErrorConsumed((athenaOneParams?: AthenaOneParams) => {
  // ANETUSERNAME is set as login_hint only for non-sso web logins by the monolith.
  // If not set, then we check for presence of LOGOUT type banner set by the monolith
  // for a non-sso type login.
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('login_hint') === 'ANETUSERNAME') {
    return true;
  }
  return isAthenaOneWebLogout(athenaOneParams) && !isAthenaOneWebSso(athenaOneParams);
});
